import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { getUserSpaces, deleteUserAccount, removeMember } from '../../api/memberApi';

// ── Styles ────────────────────────────────────────────────────────────────────

const C = {
  navy:   '#0a326d',
  red:    '#dc2626',
  redBg:  '#fef2f2',
  border: '#e2e8f0',
  muted:  '#64748b',
  dark:   '#1e293b',
};

const btnBase = {
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: 13,
  px: 2,
  py: 0.875,
};

const redBtn = {
  ...btnBase,
  bgcolor: C.red,
  color:   'white',
  '&:hover':    { bgcolor: '#b91c1c' },
  '&.Mui-disabled': { bgcolor: 'rgba(220,38,38,0.35)', color: 'white' },
};

const outlinedBtn = {
  ...btnBase,
  borderColor: C.border,
  color: C.dark,
  '&:hover': { bgcolor: '#f8fafc' },
};

const redOutlinedBtn = {
  ...btnBase,
  borderColor: C.red,
  color: C.red,
  '&:hover': { bgcolor: C.redBg },
};

// ── Sub-components ────────────────────────────────────────────────────────────

function WarningBox({ children }) {
  return (
    <Box
      sx={{
        display: 'flex', gap: 1.5, alignItems: 'flex-start',
        bgcolor: C.redBg, border: `1px solid #fecaca`,
        borderRadius: '10px', p: 1.75, mt: 1,
      }}
    >
      <WarningAmberRoundedIcon sx={{ color: C.red, fontSize: 20, flexShrink: 0, mt: 0.1 }} />
      <Typography sx={{ fontSize: 13, color: '#7f1d1d', lineHeight: 1.6 }}>
        {children}
      </Typography>
    </Box>
  );
}

function SpaceList({ spaces, currentSpaceId }) {
  return (
    <Box
      component="ul"
      sx={{ m: 0, pl: 2.5, mt: 1, display: 'flex', flexDirection: 'column', gap: 0.4 }}
    >
      {spaces.map((s) => (
        <Box component="li" key={s.spaceId}
          sx={{ fontSize: 13, color: C.dark, fontWeight: s.spaceId === currentSpaceId ? 700 : 400 }}
        >
          {s.spaceName || `Space ${s.spaceId}`}
          {s.spaceId === currentSpaceId && (
            <Box component="span"
              sx={{ ml: 1, fontSize: 10, fontWeight: 700, color: C.muted,
                    textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              current
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Props:
 *   open                  bool
 *   member                { userId, name } | null
 *   spaceId               number  — current active space
 *   onClose               () => void
 *   onRemovedFromSpace    (userId) => void
 *   onDeletedPermanently  (userId) => void
 */
export default function DeleteMemberDialog({
  open,
  member,
  spaceId,
  onClose,
  onRemovedFromSpace,
  onDeletedPermanently,
}) {
  // 'loading' | 'error' | 'single' | 'multi' | 'confirm-all'
  const [step,    setStep]    = useState('loading');
  const [spaces,  setSpaces]  = useState([]);
  const [busy,    setBusy]    = useState(false);

  // Fetch spaces when dialog opens
  useEffect(() => {
    if (!open || !member?.userId) return;

    setStep('loading');
    setSpaces([]);

    getUserSpaces(spaceId, member.userId)
      .then((data) => {
        const list = data.spaces ?? [];
        setSpaces(list);
        setStep(list.length <= 1 ? 'single' : 'multi');
      })
      .catch(() => setStep('error'));
  }, [open, member?.userId, spaceId]);

  // Reset when closed so next open starts fresh
  const handleClose = () => {
    if (busy) return;
    setStep('loading');
    setSpaces([]);
    onClose();
  };

  // Remove from this space only
  const handleRemoveFromSpace = async () => {
    setBusy(true);
    try {
      await removeMember(spaceId, member.userId);
      onRemovedFromSpace(member.userId);
    } finally {
      setBusy(false);
    }
  };

  // Permanently delete account (used by both 'single' and 'confirm-all')
  const handleDeletePermanently = async () => {
    setBusy(true);
    try {
      await deleteUserAccount(spaceId, member.userId);
      onDeletedPermanently(member.userId);
    } finally {
      setBusy(false);
    }
  };

  const name = member?.name || 'This member';
  const count = spaces.length;

  // ── Render ─────────────────────────────────────────────────────────────────

  const renderContent = () => {
    // Loading
    if (step === 'loading') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                   gap: 1.5, py: 3 }}>
          <CircularProgress size={28} sx={{ color: C.navy }} />
          <Typography sx={{ fontSize: 13, color: C.muted }}>
            Checking membership…
          </Typography>
        </Box>
      );
    }

    // Error
    if (step === 'error') {
      return (
        <Box sx={{ py: 1 }}>
          <Typography sx={{ fontSize: 13, color: C.muted }}>
            Could not load membership information. Please close and try again.
          </Typography>
        </Box>
      );
    }

    // Single space — permanent delete only
    if (step === 'single') {
      return (
        <Box>
          <Typography sx={{ fontSize: 13, color: C.dark, lineHeight: 1.7 }}>
            <strong>{name}</strong> is only a member of this space.
          </Typography>
          <WarningBox>
            Removing them will <strong>permanently delete their account</strong> from
            the entire system. This action cannot be undone.
          </WarningBox>
        </Box>
      );
    }

    // Multi space — choose action
    if (step === 'multi') {
      return (
        <Box>
          <Typography sx={{ fontSize: 13, color: C.dark, lineHeight: 1.7 }}>
            <strong>{name}</strong> belongs to <strong>{count} spaces</strong>:
          </Typography>
          <SpaceList spaces={spaces} currentSpaceId={spaceId} />
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.muted,
                            textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
            Choose what to do
          </Typography>
        </Box>
      );
    }

    // Confirm delete from all spaces
    if (step === 'confirm-all') {
      return (
        <Box>
          <Typography sx={{ fontSize: 13, color: C.dark, lineHeight: 1.7 }}>
            This will remove <strong>{name}</strong> from all{' '}
            <strong>{count} spaces</strong> and permanently delete their account.
          </Typography>
          <WarningBox>
            This action <strong>cannot be undone</strong>. The user will be immediately
            logged out and will no longer be able to access the system.
          </WarningBox>
        </Box>
      );
    }

    return null;
  };

  const renderActions = () => {
    if (step === 'loading') return null;

    if (step === 'error') {
      return (
        <Button variant="outlined" onClick={handleClose} sx={outlinedBtn}>
          Close
        </Button>
      );
    }

    if (step === 'single') {
      return (
        <>
          <Button variant="outlined" onClick={handleClose} disabled={busy} sx={outlinedBtn}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDeletePermanently}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
            sx={redBtn}
          >
            {busy ? 'Deleting…' : 'Delete Permanently'}
          </Button>
        </>
      );
    }

    if (step === 'multi') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleRemoveFromSpace}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} sx={{ color: C.dark }} /> : null}
            sx={outlinedBtn}
          >
            {busy ? 'Removing…' : 'Remove from this space only'}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setStep('confirm-all')}
            disabled={busy}
            sx={redOutlinedBtn}
          >
            Remove from all spaces &amp; delete account
          </Button>
          <Button variant="text" fullWidth onClick={handleClose} disabled={busy}
            sx={{ ...btnBase, color: C.muted }}>
            Cancel
          </Button>
        </Box>
      );
    }

    if (step === 'confirm-all') {
      return (
        <>
          <Button variant="outlined" onClick={() => setStep('multi')} disabled={busy}
            sx={outlinedBtn}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleDeletePermanently}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
            sx={redBtn}
          >
            {busy ? 'Deleting…' : 'Yes, Delete Permanently'}
          </Button>
        </>
      );
    }

    return null;
  };

  const titleMap = {
    loading:       'Remove Member',
    error:         'Remove Member',
    single:        '⚠️ Permanently Delete Account',
    multi:         'Remove Member',
    'confirm-all': '⚠️ Confirm Permanent Deletion',
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '14px' } }}
    >
      <DialogTitle
        sx={{
          fontSize: 15, fontWeight: 800, color: C.dark,
          pb: 0.5, pt: 2.5, px: 3,
        }}
      >
        {titleMap[step] ?? 'Remove Member'}
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1.5, pb: 1 }}>
        {renderContent()}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3, pb: 2.5, pt: 1,
          flexDirection: step === 'multi' ? 'column' : 'row',
          gap: step === 'multi' ? 0 : 1,
        }}
      >
        {renderActions()}
      </DialogActions>
    </Dialog>
  );
}
